import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField, Alert, Chip, IconButton, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Member-facing duty swap / cover requests. Hidden unless member_swaps_enabled
// is on. Mirrors the admin swap/cover logic, member-initiated, with a confirm
// workflow handled server-side.

const ROLE_LABEL = {
  '1st_safety_officer': '1st Safety Officer',
  '2nd_safety_officer': '2nd Safety Officer',
  administrator: 'Administrator',
};
const ROLE_TO_DUTY = ROLE_LABEL;

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('memberToken') || sessionStorage.getItem('adminToken') || ''}`,
});

const fmtAssignment = (a) => {
  if (!a) return '';
  const d = new Date(a.session_date + 'T12:00:00');
  const date = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = (a.session_time || '').slice(0, 5);
  return `${date} · ${time} · ${ROLE_LABEL[a.role_type] || a.role_type}`;
};

const STATUS_COLOR = { applied: 'success', reverted: 'default' };

function MemberSwaps({ member }) {
  const [enabled, setEnabled] = useState(false);
  const [options, setOptions] = useState({ me: null, members: [], assignments: [] });
  const [requests, setRequests] = useState([]);
  const [myMemberId, setMyMemberId] = useState(null);
  const [open, setOpen] = useState(false);
  const [counterparty, setCounterparty] = useState(null);
  const [myDuty, setMyDuty] = useState(null);
  const [theirDuty, setTheirDuty] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      // A 403 from swap-options means the feature isn't enabled for this member
      // (neither globally on nor on the test allow-list) — render nothing.
      const optRes = await fetch('/api/member/swap-options', { headers: authHeaders() });
      if (!optRes.ok) { setEnabled(false); return; }
      setEnabled(true);
      setOptions(await optRes.json());
      const reqs = await fetch('/api/member/swap-requests', { headers: authHeaders() }).then(r => r.ok ? r.json() : null);
      if (reqs) { setRequests(reqs.requests || []); setMyMemberId(reqs.myMemberId); }
    } catch (e) { /* non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setCounterparty(null); setMyDuty(null); setTheirDuty(null); setNote(''); setMsg(null); };
  const openNew = () => { resetForm(); setOpen(true); };

  const myAssignments = (options.assignments || []).filter(a => a.member_id === options.me?.id);
  const theirAssignments = (options.assignments || []).filter(a => counterparty && a.member_id === counterparty.id);

  // Validation: at least one duty; role compatibility.
  const validation = () => {
    if (!counterparty) return { error: 'Pick the other member.' };
    if (!myDuty && !theirDuty) return { error: 'Choose a duty to give up, or one of theirs to take on.' };
    if (myDuty && theirDuty && myDuty.role_type !== theirDuty.role_type) return { error: 'Both duties must be the same role to swap.' };
    if (myDuty && !theirDuty) {
      const need = ROLE_TO_DUTY[myDuty.role_type];
      if (need && (counterparty.duty_type || '').trim() !== need) return { error: `${counterparty.first_name} is not a ${need}.` };
    }
    if (!myDuty && theirDuty) {
      const need = ROLE_TO_DUTY[theirDuty.role_type];
      if (need && (options.me?.duty_type || '').trim() !== need) return { error: `You are not a ${need}.` };
    }
    return { mode: myDuty && theirDuty ? 'swap' : 'cover' };
  };

  const submit = async () => {
    const v = validation();
    if (!v.mode) return;
    setSaving(true);
    try {
      const body = {
        counterpartyMemberId: counterparty.id,
        initiatorAssignmentId: myDuty ? myDuty.id : null,
        counterpartyAssignmentId: theirDuty ? theirDuty.id : null,
        note: note || null,
      };
      const res = await fetch('/api/member/swap-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  const revertRequest = async (id) => {
    if (!window.confirm('Revert this swap? This undoes the change and notifies the other member.')) return;
    const res = await fetch(`/api/member/swap-requests/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not revert'); }
    load();
  };

  // Members have 7 days from when a swap was applied to revert it.
  const revertDeadline = (r) => new Date(new Date(r.created_at).getTime() + 7 * 86400000);
  const canRevert = (r) => r.status === 'applied' && revertDeadline(r).getTime() > Date.now();
  const deadlineLabel = (r) => revertDeadline(r).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

  const describeRequest = (r) => {
    const initiator = `${r.i_first} ${r.i_last}`.trim();
    const cpName = `${r.c_first} ${r.c_last}`.trim();
    const iA = r.i_date ? fmtAssignment({ session_date: r.i_date, session_time: r.i_time, role_type: r.i_role }) : null;
    const cA = r.c_date ? fmtAssignment({ session_date: r.c_date, session_time: r.c_time, role_type: r.c_role }) : null;
    if (iA && cA) return `${initiator} (${iA}) ↔ ${cpName} (${cA})`;
    if (iA && !cA) return `${cpName} to cover ${initiator}'s duty — ${iA}`;
    return `${initiator} to cover ${cpName}'s duty — ${cA}`;
  };

  if (!enabled) return null;
  const v = validation();

  return (
    <Paper sx={{ p: 2, mt: 3, border: '1px solid #e0e0e0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54' }}>
          Duty swaps &amp; cover
        </Typography>
        <Button variant="contained" size="small" onClick={openNew} sx={{ backgroundColor: '#0d2c54' }}>
          Request a swap
        </Button>
      </Box>

      {requests.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
          No swap requests yet. Use “Request a swap” to give up a duty you can’t do, or to take on an extra one.
        </Typography>
      ) : (
        requests.map(r => {
          const mine = r.initiator_member_id === myMemberId;
          return (
            <Box key={r.id} sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>{describeRequest(r)}</Typography>
                <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'} />
                {canRevert(r) && (
                  <Button size="small" color="error" onClick={() => revertRequest(r.id)}>Revert</Button>
                )}
              </Box>
              {r.status === 'applied' && (
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {canRevert(r)
                    ? (mine
                        ? `Applied. ${r.c_first} can revert until ${deadlineLabel(r)}.`
                        : `A swap was applied to your duties — revert by ${deadlineLabel(r)} if you didn’t agree.`)
                    : 'Applied — the 7-day revert window has passed.'}
                </Typography>
              )}
            </Box>
          );
        })
      )}

      <Dialog open={open} onClose={() => { setOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#0d2c54', fontWeight: 600 }}>
          Request a duty swap or cover
          <IconButton onClick={() => { setOpen(false); resetForm(); }} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f1f6ff', borderRadius: 1, border: '1px solid #d6e4ff' }}>
            <Typography variant="body2" sx={{ color: '#555' }}>
              Pick the other member, then choose <strong>your</strong> duty to give up, or leave it blank and pick <strong>their</strong> duty to
              take on an extra one. Fill both to do a straight swap. The change applies straight away — the other member gets an email and can revert it if they didn’t agree.
            </Typography>
          </Box>
          <Autocomplete
            size="small" sx={{ mb: 2 }}
            options={options.members || []}
            getOptionLabel={(m) => m ? `${m.first_name || ''} ${m.name || ''}`.trim() : ''}
            isOptionEqualToValue={(o, val) => o && val && o.id === val.id}
            value={counterparty}
            onChange={(_e, val) => { setCounterparty(val); setTheirDuty(null); }}
            renderInput={(p) => <TextField {...p} label="Other member" />}
          />
          <Autocomplete
            size="small" sx={{ mb: 2 }}
            options={myAssignments}
            getOptionLabel={fmtAssignment}
            isOptionEqualToValue={(o, val) => o && val && o.id === val.id}
            value={myDuty}
            onChange={(_e, val) => setMyDuty(val)}
            renderInput={(p) => <TextField {...p} label="Your duty to give up (optional)" />}
            noOptionsText="You have no upcoming duties"
          />
          <Autocomplete
            size="small" sx={{ mb: 2 }}
            disabled={!counterparty}
            options={theirAssignments}
            getOptionLabel={fmtAssignment}
            isOptionEqualToValue={(o, val) => o && val && o.id === val.id}
            value={theirDuty}
            onChange={(_e, val) => setTheirDuty(val)}
            renderInput={(p) => <TextField {...p} label="Their duty to take on (optional)" />}
            noOptionsText={counterparty ? 'No upcoming duties for this member' : 'Pick the other member first'}
          />
          <TextField
            size="small" fullWidth multiline minRows={2}
            label="Note to them (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {msg && <Alert severity="error" sx={{ mt: 2 }}>{msg}</Alert>}
          {v.error && counterparty && (myDuty || theirDuty) && <Alert severity="warning" sx={{ mt: 2 }}>{v.error}</Alert>}
          {v.mode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {v.mode === 'swap'
                ? `Swap: you and ${counterparty.first_name} exchange the two duties.`
                : (myDuty
                  ? `Cover: ${counterparty.first_name} will take your duty on ${fmtAssignment(myDuty)}.`
                  : `Cover: you will take ${counterparty.first_name}'s duty on ${fmtAssignment(theirDuty)}.`)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); resetForm(); }} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving || !v.mode} sx={{ backgroundColor: '#0d2c54' }}>
            {saving ? 'Sending…' : 'Send request'}
          </Button>
        </DialogActions>
      </Dialog>
      <Divider sx={{ mt: 1 }} />
      <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
        Swaps apply immediately; the other member can revert if they didn’t agree.
      </Typography>
    </Paper>
  );
}

export default MemberSwaps;
